// Basemap tile providers offered in "Basic" render mode. All are usable
// for commercial purposes with attribution; CartoDB layers are CC-BY 3.0,
// ESRI World Imagery is included with its required credit line.
//
// Backend rendering uses the same `id` to pick the right provider when
// stitching tiles, so keep ids stable — they're persisted in saved
// generation settings.
export const BASIC_STYLES = [
  {
    id: 'positron_nolabels',
    label: 'Positron',
    sublabel: 'Light, no labels',
    url: 'https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}.png',
    attribution: '© OpenStreetMap contributors © CARTO',
    subdomains: 'abcd',
    maxZoom: 19,
  },
  {
    id: 'positron',
    label: 'Positron',
    sublabel: 'Light, with labels',
    url: 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png',
    attribution: '© OpenStreetMap contributors © CARTO',
    subdomains: 'abcd',
    maxZoom: 19,
  },
  {
    id: 'voyager_nolabels',
    label: 'Voyager',
    sublabel: 'Mid-tone, no labels',
    url: 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager_nolabels/{z}/{x}/{y}.png',
    attribution: '© OpenStreetMap contributors © CARTO',
    subdomains: 'abcd',
    maxZoom: 19,
  },
  {
    id: 'voyager',
    label: 'Voyager',
    sublabel: 'Mid-tone, with labels',
    url: 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png',
    attribution: '© OpenStreetMap contributors © CARTO',
    subdomains: 'abcd',
    maxZoom: 19,
  },
  {
    id: 'darkmatter_nolabels',
    label: 'Dark Matter',
    sublabel: 'Dark, no labels',
    url: 'https://{s}.basemaps.cartocdn.com/dark_nolabels/{z}/{x}/{y}.png',
    attribution: '© OpenStreetMap contributors © CARTO',
    subdomains: 'abcd',
    maxZoom: 19,
  },
  {
    id: 'darkmatter',
    label: 'Dark Matter',
    sublabel: 'Dark, with labels',
    url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png',
    attribution: '© OpenStreetMap contributors © CARTO',
    subdomains: 'abcd',
    maxZoom: 19,
  },
  {
    id: 'esri_imagery',
    label: 'Satellite',
    sublabel: 'Aerial imagery',
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attribution: '© Esri — Source: Esri, Maxar, Earthstar Geographics',
    maxZoom: 19,
  },
];

export const DEFAULT_BASIC_STYLE_ID = 'positron_nolabels';

export const getBasicStyle = (id) =>
  BASIC_STYLES.find((s) => s.id === id) || BASIC_STYLES[0];

// Threshold above which "Fully customizable" mode is unavailable and the
// user must use a Basic style. Mirrors the backend rule, so keep both in
// sync if we ever change it.
export const BASIC_ONLY_KM2_THRESHOLD = 50000;

// Display formatting for km² values shown next to the slider/input.
// Ranges:
//   1 - 999      → "<n> km²"
//   1000 - 999999 → "<n>k km²"  (one decimal when not exactly round)
//   >= 1000000  → "<n>m km²"
export const formatKm2 = (km2) => {
  const n = Number(km2);
  if (!Number.isFinite(n) || n < 0) return `${km2} km²`;
  if (n < 1000) return `${Math.round(n)} km²`;
  if (n < 1_000_000) {
    const k = n / 1000;
    const txt = k >= 100 || Number.isInteger(k)
      ? `${Math.round(k)}`
      : k.toFixed(1).replace(/\.0$/, '');
    return `${txt}k km²`;
  }
  const m = n / 1_000_000;
  const txt = m >= 100 || Number.isInteger(m)
    ? `${Math.round(m)}`
    : m.toFixed(1).replace(/\.0$/, '');
  return `${txt}m km²`;
};
